import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2, Tags as TagsIcon, FolderKanban, Loader2, Pencil } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/misc';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import { apiFetch } from '@/lib/api';
import { useT } from '@/lib/i18n';
import type { Taxon } from '@/lib/contacts';

/** slug do "key" a partir do nome (a API espera key + name). */
function slugify(name: string): string {
  return name.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
}

const SWATCHES = ['#3c9a18', '#2563eb', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#64748b'];

function TaxonManager({ kind, endpoint }: { kind: 'tags' | 'groups'; endpoint: string }) {
  const t = useT();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [color, setColor] = useState(SWATCHES[0]);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<Taxon | null>(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: [endpoint],
    queryFn: async (): Promise<Taxon[]> => ((await apiFetch('GET', `/${endpoint}`)).data as { data?: Taxon[] })?.data ?? [],
  });
  const refresh = () => qc.invalidateQueries({ queryKey: [endpoint] });

  const create = async () => {
    const nm = name.trim();
    if (!nm) return;
    const key = slugify(nm);
    if (!key) { toast.error(t('taxonomies.invalidName')); return; }
    setSaving(true);
    const r = await apiFetch('POST', `/${endpoint}`, { key, name: nm, color });
    setSaving(false);
    if (!r.ok) { toast.error(r.status === 409 ? t('taxonomies.exists') : t('taxonomies.createFailed')); return; }
    setName('');
    toast.success(t('taxonomies.created'));
    refresh();
  };

  const remove = async (id: string) => {
    const r = await apiFetch('DELETE', `/${endpoint}/${id}`);
    if (!r.ok) { toast.error(t('taxonomies.deleteFailed')); return; }
    toast.success(t('taxonomies.deleted'));
    refresh();
  };

  const Icon = kind === 'tags' ? TagsIcon : FolderKanban;

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-5">
        <div className="flex items-center gap-2">
          <Icon className="size-4 text-primary" />
          <h2 className="text-base font-semibold">{kind === 'tags' ? t('taxonomies.tagsTitle') : t('taxonomies.groupsTitle')}</h2>
        </div>

        {/* Formulário de criação */}
        <div className="flex flex-col gap-2 rounded-md border bg-muted/30 p-3">
          <Label>{kind === 'tags' ? t('taxonomies.newTag') : t('taxonomies.newGroup')}</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
              placeholder={t('taxonomies.namePlaceholder')}
              className="min-w-[10rem] flex-1"
            />
            <div className="flex items-center gap-1">
              {SWATCHES.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  aria-label={c}
                  className={'size-6 rounded-full border-2 transition-transform ' + (color === c ? 'scale-110 border-foreground' : 'border-transparent')}
                  style={{ background: c }}
                />
              ))}
            </div>
            <Button onClick={create} disabled={saving || !name.trim()}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
              {t('common.create')}
            </Button>
          </div>
        </div>

        {/* Lista */}
        {isLoading ? (
          <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : items.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t('taxonomies.empty')}</p>
        ) : (
          <ul className="flex flex-col divide-y">
            {items.map((it) => (
              <li key={it.id} className="flex items-center gap-3 py-2.5">
                <span className="size-3 shrink-0 rounded-full" style={{ background: it.color || '#94a3b8' }} />
                <div className="flex min-w-0 flex-1 flex-col">
                  {it.name
                    ? <span className="truncate font-medium">{it.name}</span>
                    : <span className="truncate italic text-muted-foreground">{t('taxonomies.noLabel')}</span>}
                  {/* A chave (pode ser um UUID que o cliente mandou no disparo) — dá pra identificar e apelidar. */}
                  <span className="truncate font-mono text-xs text-muted-foreground" title={it.key}>{it.key}</span>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">{t('taxonomies.usedBy', { n: it.count ?? 0 })}</span>
                <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground" onClick={() => setEditing(it)} aria-label={t('taxonomies.edit')}>
                  <Pencil className="size-4" />
                </Button>
                <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive" onClick={() => remove(it.id)} aria-label={t('taxonomies.delete')}>
                  <Trash2 className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {editing && (
        <EditTaxonDialog
          endpoint={endpoint}
          taxon={editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
    </Card>
  );
}

/** Dá/edita o apelido (nome) e a cor de uma tag/grupo existente — reusa o upsert
 * por chave (a chave não muda; só o rótulo amigável). Essencial para nomear os
 * grupos que chegam como UUID opaco no `groups[]` do disparo. */
function EditTaxonDialog({ endpoint, taxon, onClose, onSaved }: {
  endpoint: string; taxon: Taxon; onClose: () => void; onSaved: () => void;
}) {
  const t = useT();
  const [name, setName] = useState(taxon.name);
  const [color, setColor] = useState(taxon.color || SWATCHES[0]);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const r = await apiFetch('POST', `/${endpoint}`, { key: taxon.key, name: name.trim(), color });
    setSaving(false);
    if (!r.ok) { toast.error(t('taxonomies.saveFailed')); return; }
    toast.success(t('taxonomies.saved'));
    onSaved();
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('taxonomies.editTitle')}</DialogTitle>
          <DialogDescription className="truncate font-mono">{taxon.key}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>{t('taxonomies.labelName')}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} placeholder={t('taxonomies.namePlaceholder')} autoFocus />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t('taxonomies.color')}</Label>
            <div className="flex items-center gap-1">
              {SWATCHES.map((c) => (
                <button key={c} onClick={() => setColor(c)} aria-label={c}
                  className={'size-6 rounded-full border-2 transition-transform ' + (color === c ? 'scale-110 border-foreground' : 'border-transparent')}
                  style={{ background: c }} />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="ghost">{t('common.cancel')}</Button></DialogClose>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="size-4 animate-spin" />}{t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Taxonomies() {
  const t = useT();
  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-muted-foreground">{t('taxonomies.subtitle')}</p>
      <div className="grid gap-6 lg:grid-cols-2">
        <TaxonManager kind="tags" endpoint="tags" />
        <TaxonManager kind="groups" endpoint="contact-groups" />
      </div>
    </div>
  );
}
